import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import {
  Header,
  Icon,
  Segment,
  Form,
  Tab,
  Table,
  Label,
  Message,
} from 'semantic-ui-react';
import { SG_GET_SLACK_TASK_HISTORY, SG_GET_SLACK_MY_TASK_HISTORY } from 'constants/actions';
import Navigation from './Navigation';
import { useWorkspaceId } from './useWorkspaceId';

function TaskHistory() {
  const dispatch = useDispatch();
  const workspaceId = useWorkspaceId();
  
  const { taskHistory, myTaskHistory, taskHistoryPending, myTaskHistoryPending } = useSelector(
    (s) => s.slack_tasks || {},
    shallowEqual
  );

  const currentUserId = useSelector((state) => state.auth?.employee_id, shallowEqual);

  const [dateRange, setDateRange] = useState({
    startDate: getWeekStart(),
    endDate: getWeekEnd(),
  });

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (workspaceId) {
      fetchHistory();
    }
  }, [dateRange, activeTab, workspaceId]);

  const fetchHistory = () => {
    if (!workspaceId) return;
    
    const params = new URLSearchParams({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      status: 'completed,in_progress,created',
    }).toString();

    if (activeTab === 0) {
      // My history
      dispatch({
        type: SG_GET_SLACK_MY_TASK_HISTORY,
        payload: { workspaceId, queryParams: params },
      });
    } else {
      // Team history (if you have userId to view)
      dispatch({
        type: SG_GET_SLACK_TASK_HISTORY,
        payload: { workspaceId, userId: currentUserId, queryParams: params },
      });
    }
  };

  const handleDateChange = (e) => {
    setDateRange((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const setPresetRange = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = end = formatDate(today);
        break;
      case 'week':
        start = getWeekStart();
        end = getWeekEnd();
        break;
      case 'month':
        start = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
        end = formatDate(today);
        break;
      default:
        return;
    }

    setDateRange({ startDate: start, endDate: end });
  };

  const history = activeTab === 0 ? myTaskHistory : taskHistory;
  const loading = activeTab === 0 ? myTaskHistoryPending : taskHistoryPending;
  const historyData = Array.isArray(history) ? history : [];
  const summary = history?.summary || {};
  const tasks = history?.tasks || {};

  const panes = [
    {
      menuItem: { key: 'my', icon: 'user', content: 'My History' },
      render: () => <Tab.Pane>{renderHistoryContent()}</Tab.Pane>,
    },
    {
      menuItem: { key: 'team', icon: 'users', content: 'Team History' },
      render: () => <Tab.Pane>{renderHistoryContent()}</Tab.Pane>,
    },
  ];

  function renderHistoryContent() {
    return (
      <>
        {/* Summary Cards */}
        <SummaryCards>
          <SummaryCard>
            <Icon name="tasks" size="large" color="blue" />
            <div>
              <SummaryValue>{summary.totalTasks || 0}</SummaryValue>
              <SummaryLabel>Total Tasks</SummaryLabel>
            </div>
          </SummaryCard>
          <SummaryCard>
            <Icon name="check circle" size="large" color="green" />
            <div>
              <SummaryValue>{summary.completed || 0}</SummaryValue>
              <SummaryLabel>Completed</SummaryLabel>
            </div>
          </SummaryCard>
          <SummaryCard>
            <Icon name="spinner" size="large" color="orange" />
            <div>
              <SummaryValue>{summary.inProgress || 0}</SummaryValue>
              <SummaryLabel>In Progress</SummaryLabel>
            </div>
          </SummaryCard>
          <SummaryCard>
            <Icon name="plus" size="large" color="teal" />
            <div>
              <SummaryValue>{summary.created || 0}</SummaryValue>
              <SummaryLabel>Created</SummaryLabel>
            </div>
          </SummaryCard>
        </SummaryCards>

        {/* Tasks by Category */}
        {renderTaskSection('Completed Tasks', tasks.completed, 'green')}
        {renderTaskSection('In Progress', tasks.inProgress, 'blue')}
        {renderTaskSection('Created', tasks.created, 'teal')}
      </>
    );
  }

  function renderTaskSection(title, taskList, color) {
    if (!taskList || taskList.length === 0) return null;

    return (
      <Segment>
        <Header as="h3" color={color}>
          {title} ({taskList.length})
        </Header>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Task</Table.HeaderCell>
              <Table.HeaderCell>Priority</Table.HeaderCell>
              <Table.HeaderCell>Date</Table.HeaderCell>
              <Table.HeaderCell>Assigned To</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {taskList.map((task) => (
              <Table.Row key={task.id}>
                <Table.Cell>
                  <strong>{task.title}</strong>
                  {task.description && (
                    <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                      {task.description.slice(0, 80)}
                      {task.description.length > 80 && '...'}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Label color={getPriorityColor(task.priority)}>
                    {getPriorityText(task.priority)}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  {task.completedAt || task.updatedAt || task.createdAt
                    ? new Date(
                        task.completedAt || task.updatedAt || task.createdAt
                      ).toLocaleDateString()
                    : '-'}
                </Table.Cell>
                <Table.Cell>{task.assignedTo?.name || 'Unassigned'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Segment>
    );
  }

  const getPriorityColor = (priority) => {
    const colors = { 1: 'red', 2: 'orange', 3: 'yellow', 4: 'grey' };
    return colors[priority] || 'grey';
  };

  const getPriorityText = (priority) => {
    const texts = { 1: 'Urgent', 2: 'High', 3: 'Medium', 4: 'Low' };
    return texts[priority] || 'N/A';
  };

  return (
    <Page>
      <Navigation />

      <Header as="h1">
        <Icon name="history" />
        Task History
        <Header.Subheader>
          View completed and past task activity
        </Header.Subheader>
      </Header>

      {/* Date Range Filter */}
      <Segment>
        <Form>
          <Form.Group>
            <Form.Input
              label="Start Date"
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateChange}
              width={4}
            />
            <Form.Input
              label="End Date"
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateChange}
              width={4}
            />
            <Form.Field width={8}>
              <label>Quick Select</label>
              <PresetButtons>
                <PresetBtn onClick={() => setPresetRange('today')}>Today</PresetBtn>
                <PresetBtn onClick={() => setPresetRange('week')}>This Week</PresetBtn>
                <PresetBtn onClick={() => setPresetRange('month')}>This Month</PresetBtn>
              </PresetButtons>
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      {/* Tabs */}
      {loading ? (
        <Message icon>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Loading history...</Message.Header>
          </Message.Content>
        </Message>
      ) : (
        <Tab
          panes={panes}
          activeIndex={activeTab}
          onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
        />
      )}
    </Page>
  );
}

// Helper functions
function formatDate(date) {
  return date.toISOString().split('T')[0];
}

function getWeekStart() {
  const today = new Date();
  const day = today.getDay();
  const diff = today.getDate() - day + (day === 0 ? -6 : 1);
  return formatDate(new Date(today.setDate(diff)));
}

function getWeekEnd() {
  return formatDate(new Date());
}

const Page = styled.div`
  padding: 20px;
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: bold;
  color: #2185d0;
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  color: #666;
`;

const PresetButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const PresetBtn = styled.button`
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #2185d0;
  color: #2185d0;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: #eff6ff;
  }
`;

export default TaskHistory;
